package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupRole
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.ui.profile.profileMessage
import com.swetdot.swetgram.ui.profile.QrUiState
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.discover_groups_delete_group_failed
import swetgram.shared.generated.resources.discover_groups_delete_owner_only
import swetgram.shared.generated.resources.discover_groups_group_gone
import swetgram.shared.generated.resources.discover_groups_group_load_failed
import swetgram.shared.generated.resources.discover_groups_leave_failed
import swetgram.shared.generated.resources.discover_groups_join_failed
import swetgram.shared.generated.resources.discover_groups_qr_failed
import swetgram.shared.generated.resources.discover_groups_no_permission
import swetgram.shared.generated.resources.discover_groups_owner_cannot_leave
import swetgram.shared.generated.resources.group_exit_owner_alone
import swetgram.shared.generated.resources.group_exit_owner_must_hand_over
import swetgram.shared.generated.resources.group_member_count
import swetgram.shared.generated.resources.group_role_admin
import swetgram.shared.generated.resources.group_role_owner
import swetgram.shared.generated.resources.group_visibility_private
import swetgram.shared.generated.resources.group_visibility_public
import swetgram.shared.generated.resources.handle_prefixed

/**
 * The group detail screen's state, and the copy it renders.
 *
 * Follows `ui/profile/ProfileViewModel.kt`: an `androidx.lifecycle.ViewModel`, work on
 * `viewModelScope`, state as a `StateFlow`, every `catch` on `Throwable` with
 * `CancellationException` rethrown first, and a failure reported ALONGSIDE the content rather than
 * in place of it.
 *
 * Permissions are NOT re-derived here. `domain/Group.kt` already resolves SPS §12.7's table into
 * `Group.permissions`, and a second copy in a view model is how the two start disagreeing about who
 * may delete a group. What this file adds is the one question the table cannot answer on its own:
 * an owner is refused `leave` whatever the member count, and the reason a screen must give differs
 * depending on whether there is anybody to hand the group to. See [groupExitOption].
 *
 * WHAT THIS SCREEN NOW OFFERS THAT IT DID NOT: an invite link, a private group's join queue, and
 * the ban list. Each is a pushed route rather than something rendered here, and each is gated on a
 * permission read from `Group.permissions` rather than re-derived. `transferGroupOwnership` is still
 * not offered by any screen, which is what keeps
 * [GroupExitOption.OWNER_MUST_HAND_OVER]'s sentence true.
 *
 * A WARNING THAT BELONGS ON THE RECORD RATHER THAN IN A COMMENT NOBODY READS: the three operations
 * behind those rows are NOT routed on the deployed server. `GroupRoutes.kt` installs none of the
 * nine pass-3 paths and `Module.kt` constructs neither of the two services that implement them, so
 * `POST /groups/{id}/invite-link`, `GET /groups/{id}/bans` and `GET /groups/{id}/join-requests` all
 * answer 404 -- probed against the VPS on 2026-08-06. Until the routes land, tapping any of these
 * rows reaches a screen whose first read fails, and `groupBanner` reports a 404 as "This group is no
 * longer available", which is the wrong sentence. The fix is the routes, not the copy.
 */

// ---------------------------------------------------------------------------------------------
// The rules -- pure, so they can be tested without a repository
// ---------------------------------------------------------------------------------------------

/**
 * How the caller can get out of this group.
 *
 * `Group.permissions.canLeave` answers whether `leaveGroup` will work; this splits the NO into the
 * two cases that need different words, which is the whole reason the function exists.
 *
 * DECISIONS.md §7: "The LAST owner is refused exactly like any other owner. An owner alone in their
 * group cannot leave; `deleteGroup` is the way out". Verified against the deployed server on
 * 2026-08-06: `POST /groups/{id}/leave` as the sole owner answers `409 ownership_transfer_required`
 * with the message "Transfer ownership before leaving the group." -- advice that cannot be followed
 * when there is nobody to transfer to, which is exactly why the client must not simply relay it.
 *
 * The member count is trustworthy for this: the contract bounds it at `minimum: 1`, and that lower
 * bound holds only because an owner can never walk out (V8's `groups_member_count_range`), so a
 * count of 1 alongside an owner is the last-owner case rather than a degenerate one.
 */
internal enum class GroupExitOption {
    /** A member or an admin. The write will succeed. */
    LEAVE,

    /** An owner with other members: ownership has to move first, and no screen can move it yet. */
    OWNER_MUST_HAND_OVER,

    /** The last owner. There is nobody to hand the group to, so deleting is the only exit. */
    OWNER_ALONE,
}

internal fun groupExitOption(group: Group): GroupExitOption = when {
    group.permissions.canLeave -> GroupExitOption.LEAVE
    group.memberCount <= 1 -> GroupExitOption.OWNER_ALONE
    else -> GroupExitOption.OWNER_MUST_HAND_OVER
}

/**
 * The sentence shown where the Leave button would be, or null when the button itself says it.
 *
 * SDS §18.2: say what happened and what to do next. Both owner sentences name the action that
 * actually EXISTS, and neither points at a control this build does not have -- which is what makes
 * them different from the server's own message.
 */
internal fun groupExitLine(option: GroupExitOption): UiText? = when (option) {
    GroupExitOption.LEAVE -> null
    GroupExitOption.OWNER_MUST_HAND_OVER -> Res.string.group_exit_owner_must_hand_over.text()
    GroupExitOption.OWNER_ALONE -> Res.string.group_exit_owner_alone.text()
}

/**
 * "12 members", and "1 member" for the singular the last-owner case always hits.
 *
 * WHAT THIS FUNCTION USED TO BE is the argument for the whole seam. It read
 * `if (count == 1) "1 member" else "$count members"`, which is correct English and unusable
 * Russian: 1 участник, 2 участника, 5 участников, 21 участник. `count == 1` is not a rule that
 * survives translation, and no call site can fix that by being careful. The rule now lives in the
 * resource file, where CLDR picks the form.
 *
 * The singular is not an edge case worth losing: a group the user just created has exactly one
 * member, and that is the first thing they see.
 */
internal fun memberCountLine(count: Int): UiText = Res.plurals.group_member_count.count(count)

/**
 * The line under a group's name: what kind of group it is, its public handle, and its size.
 *
 * [username] comes from `Group.publicAddress`, which is non-null only when the server sent a
 * COMPLETE public address -- `domain/Group.kt` folds the contract's `username` and `public_url` into
 * one value for that reason. A private group has none and none is invented for it.
 *
 * There is no "unknown" branch because `domain.GroupVisibility` has no unknown member: the boundary
 * resolves an unreadable value to [GroupVisibility.PRIVATE], the failing-closed direction, so this
 * function never has to decide what to say about a visibility nobody could read.
 *
 * A [UiText.Joined] rather than a format string, and that is forced rather than chosen: the last
 * part is a PLURAL, and a plural cannot be an argument to a format string because the argument is
 * substituted only after the grammatical form has already been picked.
 */
internal fun groupSubtitle(
    visibility: GroupVisibility,
    username: String?,
    memberCount: Int,
): UiText {
    val parts = buildList {
        when (visibility) {
            GroupVisibility.PUBLIC -> add(Res.string.group_visibility_public.text())
            GroupVisibility.PRIVATE -> add(Res.string.group_visibility_private.text())
        }
        // The handle is a VALUE, not copy, so it travels as a format argument.
        username?.takeIf { it.isNotBlank() }?.let { add(Res.string.handle_prefixed.text(it)) }
        add(memberCountLine(memberCount))
    }
    return UiText.Joined(parts, SUBTITLE_SEPARATOR)
}

private const val SUBTITLE_SEPARATOR = " · "

/**
 * The role tag on a member row, or null for a role that should carry none.
 *
 * SPS §12.6 asks the Members tab to show "member list; admins" -- so Owner and Admin are marked and
 * a plain member is not. Tagging every row "Member" would put a word on 498 rows of 500 to
 * distinguish the two that matter, which is the opposite of what the mark is for.
 *
 * It matters here that `domain.GroupRole` has no unknown member: the boundary maps an unreadable
 * role to [GroupRole.MEMBER], the floor, so this returns null for it and no invented seniority is
 * ever printed next to somebody's name.
 */
internal fun roleLabel(role: GroupRole): UiText? = when (role) {
    GroupRole.OWNER -> Res.string.group_role_owner.text()
    GroupRole.ADMIN -> Res.string.group_role_admin.text()
    GroupRole.MEMBER -> null
}

// ---------------------------------------------------------------------------------------------
// Group detail
// ---------------------------------------------------------------------------------------------

data class GroupUiState(
    val group: Group? = null,
    /** False until the first read settles, so an unloaded screen is not reported as unavailable. */
    val loadedOnce: Boolean = false,
    /** A leave or a delete is in flight. Disables both rather than letting a second tap through. */
    val busy: Boolean = false,
    val error: UiText? = null,
    /**
     * The caller is no longer in this group -- they left it, or they deleted it.
     *
     * A ONE-SHOT signal, exactly like `PublicProfileUiState.openChatId`: the screen pops the route
     * and calls [GroupViewModel.consumeExited], or the next recomposition pops again.
     */
    val exited: Boolean = false,
    val qr: QrUiState = QrUiState(),
) {
    /**
     * Null until a group is loaded; there is nothing to offer before then.
     *
     * `internal` because [GroupExitOption] is: the three cases are this package's own reading of
     * DECISIONS §7, not something a caller outside `:shared` should branch on. Widening the enum
     * instead would publish a vocabulary the navigation host has no use for.
     */
    internal val exitOption: GroupExitOption? get() = group?.let { groupExitOption(it) }

    /** SPS §12.7, resolved once in `domain/Group.kt` and only read here. */
    val canDelete: Boolean get() = group?.let { it.isMember && it.permissions.canDeleteGroup } == true

    val canJoin: Boolean get() = group?.let {
        !it.isMember && it.visibility == GroupVisibility.PUBLIC
    } == true

    val canOpenSettings: Boolean get() = group?.let {
        it.isMember && (it.permissions.canEditGroup || it.permissions.canEditSettings)
    } == true

    val canShowQr: Boolean get() = group?.let {
        it.isMember && (it.visibility == GroupVisibility.PUBLIC || it.permissions.canInviteMembers)
    } == true

    /**
     * Whether to offer the invite-link screen at all.
     *
     * `invite_permission`, which the server derives from visibility: everyone in a public group,
     * Owner and Admin in a private one. NOT the same question as whether they may ROTATE the link --
     * that is `canRotateInviteLink` and the invite screen asks it separately, because sharing a link
     * and destroying one are different acts behind one endpoint.
     */
    val canOpenInviteLink: Boolean get() = group?.let {
        it.isMember && it.permissions.canInviteMembers
    } == true

    /**
     * Whether to offer the join queue.
     *
     * Private groups only, and moderators only. A public group's queue is not forbidden -- the
     * server answers an empty page, because a public group joins immediately and "no pending
     * requests" is simply true of it -- but a row leading to a screen that can only ever be empty is
     * a row that teaches the user nothing. DECISION: the folder describes neither the queue nor this
     * rule; SPS §12.7 has no join-request row at all.
     */
    val canOpenJoinRequests: Boolean get() = group?.hasJoinQueue == true

    /**
     * Whether to offer the ban list. Owner and Admin, in both kinds of group.
     *
     * Shown even when nothing is banned, unlike the queue above, and the difference is deliberate: a
     * moderator needs to be able to LOOK, because a ban made from the member card is otherwise
     * invisible and there is no other screen that can lift one.
     */
    val canOpenBans: Boolean get() = group?.let { it.isMember && it.permissions.canModerate } == true
}

/**
 * SPS §12 / SDS §14. One group: who it is, how to reach its chat and its members, and the ways out.
 */
class GroupViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupUiState())
    val state: StateFlow<GroupUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
    }

    fun refresh() {
        // A second tap on Retry while the first read is in flight would produce two banners for
        // one failure.
        if (loading) return
        loading = true
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    group = groups.group(groupId),
                    loadedOnce = true,
                    error = null,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner(Res.string.discover_groups_group_load_failed.text()),
                )
            } finally {
                loading = false
            }
        }
    }

    fun join() {
        val current = _state.value
        if (current.busy || !current.canJoin) return
        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                val joined = groups.joinPublic(groupId)
                _state.value = _state.value.copy(group = joined, busy = false, error = null)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = error.groupBanner(Res.string.discover_groups_join_failed.text()),
                )
            }
        }
    }

    fun showQr() {
        val current = _state.value
        if (!current.canShowQr || current.qr.loading) return
        _state.value = current.copy(qr = QrUiState(visible = true, loading = true))
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    qr = QrUiState(visible = true, qr = groups.qr(groupId)),
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    qr = QrUiState(
                        visible = true,
                        error = error.groupBanner(Res.string.discover_groups_qr_failed.text()),
                    ),
                )
            }
        }
    }

    fun dismissQr() {
        _state.value = _state.value.copy(qr = QrUiState())
    }

    /**
     * `leaveGroup`. 204 for a member or an admin.
     *
     * The screen does not offer this to an owner, but the guard is here as well: the caller's role
     * comes from a response that could be a minute old, and somebody who was made owner in that
     * minute would otherwise send a request that can only ever 409. When it happens anyway the 409
     * is translated rather than relayed -- the server's "Transfer ownership before leaving the
     * group" names a control this build does not have.
     */
    fun leave() {
        val current = _state.value
        if (current.busy) return
        if (current.group?.isMember != true || current.exitOption != GroupExitOption.LEAVE) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.leave(groupId)
                _state.value = _state.value.copy(busy = false, exited = true)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    busy = false,
                    error = when (error.asGroupFailure()) {
                        GroupFailure.OWNERSHIP_TRANSFER_REQUIRED ->
                            Res.string.discover_groups_owner_cannot_leave.text()
                        else -> error.groupBanner(Res.string.discover_groups_leave_failed.text())
                    },
                )
                // The role that produced that answer is not the role this screen was drawn from.
                refresh()
            }
        }
    }

    /**
     * `deleteGroup`. Owner-only, 204, and permanent for everybody in the group.
     *
     * A group that is already gone counts as success rather than as a failure: it is what was asked
     * for, and reporting "could not delete" over a group that no longer exists would leave the user
     * tapping a button whose work is done. `GroupFailure.UNAVAILABLE` covers both "no such group"
     * and "you cannot see it", which are the same thing to somebody who just deleted it.
     */
    fun deleteGroup() {
        val current = _state.value
        if (current.busy || !current.canDelete) return

        _state.value = current.copy(busy = true, error = null)
        viewModelScope.launch {
            try {
                groups.delete(groupId)
                _state.value = _state.value.copy(busy = false, exited = true)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                when (error.asGroupFailure()) {
                    GroupFailure.UNAVAILABLE ->
                        _state.value = _state.value.copy(busy = false, exited = true)
                    GroupFailure.INSUFFICIENT_ROLE ->
                        _state.value = _state.value.copy(
                            busy = false,
                            error = Res.string.discover_groups_delete_owner_only.text(),
                        )
                    else ->
                        _state.value = _state.value.copy(
                            busy = false,
                            error = error.groupBanner(
                                Res.string.discover_groups_delete_group_failed.text(),
                            ),
                        )
                }
            }
        }
    }

    /** Consumed by the caller BEFORE it navigates; see [GroupUiState.exited]. */
    fun consumeExited() {
        _state.value = _state.value.copy(exited = false)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

/**
 * The banner text for a failure on a group screen.
 *
 * Two layers, and each is where it is on purpose. `asGroupFailure` is the boundary's own mapping of
 * the contract's group error codes, so a view model branches on a `GroupFailure` and never imports
 * `protocol` (IMPLEMENTATION_NOTES §10). Everything with no group meaning -- offline, a timeout, an
 * expired session, an unsupported client, a rate limit -- falls through to `ui/profile`'s
 * `profileMessage`, which already holds that copy; a second copy of that table is precisely what
 * `ProfileViewModel.withSaveFailure` says it refuses to make.
 *
 * TODO(shared-copy): `profileMessage`'s name is wrong for this caller. It belongs in a
 * `ui/ErrorCopy.kt` next to `SwetErrorBanner`, which renders everything it produces; it is reached
 * across packages here because `ui/profile` was not this change's to edit.
 */
internal fun Throwable.groupBanner(fallback: UiText): UiText = when (asGroupFailure()) {
    // One answer for "no such group" and "you are not in it", exactly as the server gives one.
    GroupFailure.UNAVAILABLE -> Res.string.discover_groups_group_gone.text()
    GroupFailure.INSUFFICIENT_ROLE -> Res.string.discover_groups_no_permission.text()
    else -> profileMessage(fallback)
}

/**
 * THE SAME TABLE, STILL RETURNING ENGLISH, FOR THE CALLERS THIS PASS DID NOT REACH.
 *
 * `GroupsViewModel`, `GroupBansViewModel`, `GroupMembersViewModel`, `GroupJoinRequestsViewModel`,
 * `GroupInviteViewModel` and `JoinGroupViewModel` all store their banner in a `String` field of
 * their own state, and moving one of them means moving its screen with it. So the old shape stays
 * as an OVERLOAD, exactly as `profileMessage` does and for the same reason: a caller becomes
 * localised by changing its argument from a literal to a key, and nothing else.
 *
 * TODO(localization): delete this overload once the other six group view models pass a [UiText].
 * It is a SECOND COPY of the two sentences above -- `CopyLintTest` sees the resource half and
 * nothing mechanical can tell that these have stopped agreeing with it.
 */
internal fun Throwable.groupBanner(fallback: String): String = when (asGroupFailure()) {
    GroupFailure.UNAVAILABLE -> "This group is no longer available."
    GroupFailure.INSUFFICIENT_ROLE -> "You do not have permission to do that."
    else -> profileMessage(fallback)
}
